/**
 * Resolve the Meta Pixel id that should actually fire on a public surface.
 *
 * A page inherits the seller's "main" pixel (from user_settings) unless it sets
 * its own override. Tracking can be switched off per page; an off page returns
 * null even when a pixel id exists. Empty strings are treated as "unset".
 */
export function resolveEffectivePixelId(opts: {
  pixelEnabled?: boolean | null;
  pagePixelId?: string | null;
  mainPixelId?: string | null;
}): string | null {
  if (opts.pixelEnabled === false) return null;
  const page = opts.pagePixelId?.trim();
  if (page) return page;
  const main = opts.mainPixelId?.trim();
  return main || null;
}

/** The name of the event sent when a buyer opens the product list of a video (FYP's yellow bag). */
export const VIEW_BAG_EVENT = "ViewBag";

export type ProductEvent = "ViewContent" | "AddToCart" | "InitiateCheckout" | "AddPaymentInfo" | "Purchase" | typeof VIEW_BAG_EVENT;

/**
 * The product fields Meta lists for each event (Meta Pixel reference, Standard
 * Events) — and no others. The custom ViewBag carries a product view's fields
 * without a value: products looked at, none reached for yet.
 */
export const EVENT_FIELDS: Record<ProductEvent, readonly string[]> = {
  ViewContent: ["content_ids", "content_type", "contents", "currency", "value"],
  AddToCart: ["content_ids", "content_type", "contents", "currency", "value"],
  InitiateCheckout: ["content_ids", "contents", "currency", "num_items", "value"],
  AddPaymentInfo: ["content_ids", "contents", "currency", "value"],
  Purchase: ["content_ids", "content_type", "contents", "currency", "num_items", "value"],
  [VIEW_BAG_EVENT]: ["content_ids", "content_type", "contents", "currency"],
};

/**
 * An event's product fields, exactly as Meta lists them for that event:
 * content_ids, contents (id, quantity, item_price), content_type, currency,
 * num_items and value, each only where Meta names it. `value` is the total
 * where there is one (a checkout, a purchase); for a product looked at or
 * reached for, its price — the cheapest of a choice.
 */
export function metaProductParams(
  event: ProductEvent,
  lines: { id: string; price: number; quantity?: number }[],
  o: { currency?: string | null; value?: number } = {},
): Record<string, unknown> {
  const all: Record<string, unknown> = {
    content_ids: lines.map((l) => l.id),
    content_type: "product",
    contents: lines.map((l) => ({ id: l.id, quantity: l.quantity ?? 1, item_price: l.price })),
    num_items: lines.reduce((n, l) => n + (l.quantity ?? 1), 0),
  };
  if (o.currency) all.currency = o.currency;
  const value = o.value ?? (lines.length ? Math.min(...lines.map((l) => l.price)) : undefined);
  if (value != null) all.value = value;
  return Object.fromEntries(EVENT_FIELDS[event].filter((k) => k in all).map((k) => [k, all[k]]));
}
